import { IRoom, RoomType } from 'Plugins/FloorPlan/Models/IRoom';
import { ConfigurationUtils } from '../../../../Helpers/ConfigurationUtils';
import { QuestionContext, QuestionContextHelper } from '../../../../Helpers/QuestionContextHelper';
import { QuestionsHelper } from '../../../../Helpers/QuestionsHelper';
import { ConfigurationReponseDto } from '../../../../Models/Projects/Configuration';
import { ProduitDto } from '../../../../Models/Questions/ProduitDto';
import { QuestionContextType, QuestionItem, QuestionStep } from '../../../../Models/Questions/QuestionDto';
import { ContextQuestionList, IMapItem } from '../../../../Plugins/FloorPlan/Models/IMapItem';
import { OpeningType } from '../../../../Plugins/FloorPlan/Models/IOpening';
import { RoomItemType } from '../../../../Plugins/FloorPlan/Models/IRoomItem';
import { ContextQuestionsUtils } from '../Utils/ContextQuestionsUtils';
import { getProduitPrincipal } from '../Utils/UpdateQuestions';
import { CreateMapItemContextParams, QuestionContextUtils } from '../Utils/QuestionContextUtils';

type PrepareItemsStepQuestions = {
    itemId: string;
    item: IMapItem;
    questions: Array<QuestionItem>;
    questionProduitPrincipal?: QuestionItem;

    //produitPrincipal?: ProduitDto;

    contextType: QuestionContextType;
    openingType?: OpeningType;
    roomItemType?: RoomItemType;

    fromLoadingConfiguration?: boolean;
    reponses?: ConfigurationReponseDto[];
    autoValidQuestionCommentType?: boolean;
    autoFillAndValidQuestionNumberType?: boolean;

    stopAfterStepProduit?: boolean;

    isVendeur?: boolean;
    withLog?: boolean;
};

type PrepareItemStepQuestions = PrepareItemsStepQuestions & { step: QuestionStep; };

export type PrepareGeneralStepQuestions = {
    contextQuestionList: ContextQuestionList;
    previousContext?: QuestionContext;
    autoValidQuestionCommentType?: boolean;
    autoFillAndValidQuestionNumberType?: boolean;
    fromLoadingConfiguration?: boolean;
    reponses?: ConfigurationReponseDto[];
    isVendeur?: boolean;
    withLog?: boolean;
};

export type PrepareStepQuestions = {
    item?: IMapItem;
    stepQuestions: ContextQuestionList;
    context?: QuestionContext;
    autoValidQuestionCommentType?: boolean;
    autoFillAndValidQuestionNumberType?: boolean;
    questionProduitPrincipal?: QuestionItem;
    isVendeur?: boolean;
    withLog?: boolean;
};

type ApplyReponseStepQuestions = {
    contextId?: string;
    questions: QuestionItem[];
    reponses: ConfigurationReponseDto[],
    produitPrincipal?: ProduitDto
}

type PrepareRoomQuestionsParams = Partial<{
    questions: QuestionItem[],
    stepHabitation?: ContextQuestionList;
    isVendeur: boolean;
    global_surface_area?: number;
    fromLoadingConfiguration: boolean;
    reponses: ConfigurationReponseDto[];
    autoValidQuestionCommentType: boolean;
    stopAfterStepProduit: boolean;
}>

export class QuestionPrepare {

    public static getPreviousStepContext = (item: IMapItem, step: QuestionStep, withLog?: boolean): QuestionContext => {
        const getLastContext = (stepContext?: ContextQuestionList) => QuestionsHelper.getLastShownQuestion(stepContext?.questions)?.lastShownQuestion?.context;
        switch (step) {
            case QuestionStep.StepTva:
                return item.questionsBaseContext || {};
            case QuestionStep.StepProduit:
                return getLastContext(item.questionsTva) || this.getPreviousStepContext(item, QuestionStep.StepTva)
            case QuestionStep.StepPose:
                return getLastContext(item.questionsProduit) || this.getPreviousStepContext(item, QuestionStep.StepProduit, withLog);
            case QuestionStep.StepSupport:
                return getLastContext(item.questionsPose) || this.getPreviousStepContext(item, QuestionStep.StepPose, withLog);
            case QuestionStep.StepPreparation:
                return getLastContext(item.questionsSupport) || this.getPreviousStepContext(item, QuestionStep.StepSupport, withLog);
            case QuestionStep.StepFinitions:
                return getLastContext(item.questionsPreparation) || this.getPreviousStepContext(item, QuestionStep.StepPreparation, withLog)
            default:
                return item.questionsBaseContext || {};
        }
    };

    public static getStepQuestions = (item: IMapItem, step: QuestionStep): ContextQuestionList | undefined => {
        switch (step) {
            case QuestionStep.StepTva:
                return item.questionsTva;
            case QuestionStep.StepProduit:
                return item.questionsProduit;
            case QuestionStep.StepPose:
                return item.questionsPose;
            case QuestionStep.StepSupport:
                return item.questionsSupport;
            case QuestionStep.StepPreparation:
                return item.questionsPreparation;
            case QuestionStep.StepFinitions:
                return item.questionsFinition;
            default:
                return undefined;
        }
    };

    public static createStepQuestions = (questions: QuestionItem[], step: QuestionStep): ContextQuestionList => {
        const stepQuestions = questions.filter((q) => q.related_step === step);
        return { step, questions: stepQuestions };
    };

    public static prepareStepsQuestions = (item: IRoom, params: PrepareRoomQuestionsParams) => {
        const { questions = [], stepHabitation, isVendeur, global_surface_area, reponses, fromLoadingConfiguration, autoValidQuestionCommentType } = params;

        const createContextParams: CreateMapItemContextParams = { global_surface_area };
        item.questionsBaseContext = QuestionContextHelper.mergeContext(
            QuestionContextHelper.getBaseQuestionsContext(stepHabitation?.questions),
            QuestionContextUtils.createRoomContext(item, createContextParams)
        );

        const updateStepsQuestionsParams = { questions, fromLoadingConfiguration, reponses, isVendeur, autoValidQuestionCommentType };
        QuestionPrepare.updateItemStepsQuestions({
            ...updateStepsQuestionsParams,
            itemId: item.roomId!,
            item,
            contextType: toQuestionContextType(item.type!),
            stopAfterStepProduit: params.stopAfterStepProduit
        });

        const { questionProduitPrincipal } = getProduitPrincipal(item);

        return { questionProduitPrincipal, item, context: item.questionsBaseContext }
    }

    public static toStepQuestions = ({
        step,
        itemId,
        item,
        contextType,
        openingType,
        roomItemType,
        questions = [],
        questionProduitPrincipal,
        fromLoadingConfiguration,
        autoValidQuestionCommentType = false,
        autoFillAndValidQuestionNumberType = true,
        reponses = [],
        isVendeur,
        withLog
    }: PrepareItemStepQuestions) => {

        const params: Partial<PrepareStepQuestions> = { isVendeur, autoValidQuestionCommentType, autoFillAndValidQuestionNumberType, questionProduitPrincipal };

        // Fix completion when product step not applicable #51209
        let isCurrentStepApplicable = true;
        if (step === QuestionStep.StepProduit) {
            isCurrentStepApplicable = [
                QuestionContextType.Rooms,
                QuestionContextType.Walls,
                QuestionContextType.Stairs
            ].includes(contextType);
            contextType = QuestionContextType.General;
        }

        const stepQuestions = isCurrentStepApplicable ? questions.filter((q) => q.related_step === step) : [];
        const questionsToUse = this.getStepQuestions(item, step) || ContextQuestionsUtils.toContextQuestions({ step, questions: stepQuestions, contextType, openingType, roomItemType });

        if (fromLoadingConfiguration) {
            this.applyReponseStepQuestions({ contextId: itemId, questions: questionsToUse.questions, reponses, produitPrincipal: questionProduitPrincipal?.produitValue });
        }

        const contextQuestions = this.updateStepQuestions({
            ...params,
            item,
            stepQuestions: questionsToUse,
            context: this.getPreviousStepContext(item, step),
            withLog
        });
        return contextQuestions;
    };

    public static updateItemStepsQuestions = ({ item, questionProduitPrincipal, ...params }: PrepareItemsStepQuestions) => {
        item.questionsTva = this.toStepQuestions({ step: QuestionStep.StepTva, item, ...params });


        questionProduitPrincipal = questionProduitPrincipal ?? getProduitPrincipal(item).questionProduitPrincipal;
        item.questionsProduit = this.toStepQuestions({ step: QuestionStep.StepProduit, item, ...params, questionProduitPrincipal });
        if (params.stopAfterStepProduit) {
            return;
        }

        item.questionsPose = this.toStepQuestions({ step: QuestionStep.StepPose, item, ...params, questionProduitPrincipal });
        item.questionsSupport = this.toStepQuestions({ step: QuestionStep.StepSupport, item, ...params, questionProduitPrincipal });
        item.questionsPreparation = this.toStepQuestions({ step: QuestionStep.StepPreparation, item, ...params, questionProduitPrincipal });
        item.questionsFinition = this.toStepQuestions({ step: QuestionStep.StepFinitions, item, ...params, questionProduitPrincipal });
    };

    public static updateGeneralStepsQuestions = ({
        contextQuestionList,
        previousContext = {},
        fromLoadingConfiguration,
        autoValidQuestionCommentType = false,
        autoFillAndValidQuestionNumberType,
        reponses = [],
        isVendeur,
    }: PrepareGeneralStepQuestions) => {
        if (fromLoadingConfiguration) {
            this.applyReponseStepQuestions({ questions: contextQuestionList.questions, reponses });
        }
        return this.updateStepQuestions({ stepQuestions: contextQuestionList, autoValidQuestionCommentType, autoFillAndValidQuestionNumberType, context: previousContext, isVendeur });
    };

    public static applyReponseStepQuestions = ({ contextId, questions = [], reponses = [], produitPrincipal }
        : ApplyReponseStepQuestions) => {
        questions.forEach((question: QuestionItem) => {
            ConfigurationUtils.applyReponseToQuestion({
                contextId,
                produitPrincipal,
                question: question,
                reponses,
            });
        });
    };

    public static updateStepQuestions = ({
        item,
        stepQuestions,
        context = {},
        questionProduitPrincipal,
        autoValidQuestionCommentType,
        autoFillAndValidQuestionNumberType = true,
        withLog,
        isVendeur,
    }: PrepareStepQuestions): ContextQuestionList => {
        stepQuestions.initialContext = context;
        const questions = stepQuestions?.questions || [];

        if (questions.length > 0) {
            QuestionsHelper.autoValidateQuestionIfNeeded({ questions, autoValidQuestionCommentType, autoFillAndValidQuestionNumberType });
            QuestionsHelper.initializeQuestions({ questions, questionProduitPrincipal, initialContext: context, withLog });
            const findIncompatibility = QuestionsHelper.findFirstIncompatibility(questions);
            let { endQuestion, isEmpty } = QuestionsHelper.isStepCompleted({ item, questions, produitPrincipal: questionProduitPrincipal?.produitValue, isVendeur });

            //* update questions and context
            stepQuestions.questions = questions;
            stepQuestions.endQuestion = findIncompatibility.findIncompatibility ? false : endQuestion;
            stepQuestions.isEmpty = isEmpty;
            stepQuestions.findIncompatibility = findIncompatibility.findIncompatibility
                ? findIncompatibility
                : undefined;
        } else {
            stepQuestions.questions = questions;
            stepQuestions.endQuestion = true;
            stepQuestions.isEmpty = true;
            stepQuestions.findIncompatibility = undefined;
        }
        return stepQuestions;
    };

    public static setFirstIncompatibility = (item: IMapItem, rooms: IMapItem[] = []) => {
        const firstIncompatibility = QuestionsHelper.findFirstIncompatibility([
            ...(item.questionsTva?.questions || []),
            ...(item.questionsProduit?.questions || []),
            ...(item.questionsPose?.questions || []),
            ...(item.questionsSupport?.questions || []),
            ...(item.questionsPreparation?.questions || []),
            ...(item.questionsFinition?.questions || []),
        ]);

        if (firstIncompatibility.findIncompatibility) {
            item.findIncompatibilityResult = firstIncompatibility;
            rooms.find((r) => r.roomId === item.roomId)!.findIncompatibilityResult = firstIncompatibility;
        } else {
            item.findIncompatibilityResult = undefined;
        }
    }

    //#region //* SECTION

    public static allPreviousStepCompleted = (item: IMapItem, step: QuestionStep): boolean | undefined => {
        const prevStep = this.getPreviousStep(item, step);
        const prevStepEnd = prevStep ? prevStep?.endQuestion : true;
        switch (step) {
            case QuestionStep.StepPose:
                return prevStepEnd && this.allPreviousStepCompleted(item, QuestionStep.StepProduit);
            case QuestionStep.StepSupport:
                return prevStepEnd && this.allPreviousStepCompleted(item, QuestionStep.StepPose);
            case QuestionStep.StepPreparation:
                return prevStepEnd && this.allPreviousStepCompleted(item, QuestionStep.StepSupport);
            case QuestionStep.StepFinitions:
                return prevStepEnd && this.allPreviousStepCompleted(item, QuestionStep.StepPreparation);
            default:
                return prevStepEnd;
        }
    };

    public static isStepQuestionsOpened = (item?: IMapItem, step?: QuestionStep) => {
        switch (step) {
            case QuestionStep.StepProduit:
                return item?.openSections?.questionsProduit;
            case QuestionStep.StepPose:
                return item?.openSections?.questionsPose;
            case QuestionStep.StepSupport:
                return item?.openSections?.questionsSupport;
            case QuestionStep.StepPreparation:
                return item?.openSections?.questionsPreparation;
            case QuestionStep.StepFinitions:
                return item?.openSections?.questionsFinition;
            default:
                return item?.openSections?.infos;
        }
    };

    public static getPreviousStep = (item: IMapItem, step: QuestionStep): ContextQuestionList | undefined => {
        switch (step) {
            case QuestionStep.StepPose:
                return item.questionsProduit?.endQuestion ? this.getPreviousStep(item, QuestionStep.StepProduit) : item.questionsProduit;
            case QuestionStep.StepSupport:
                return item.questionsPose?.endQuestion ? this.getPreviousStep(item, QuestionStep.StepPose) : item.questionsPose;
            case QuestionStep.StepPreparation:
                return item.questionsSupport?.endQuestion ? this.getPreviousStep(item, QuestionStep.StepSupport) : item.questionsSupport;
            case QuestionStep.StepFinitions:
                return item.questionsPreparation?.endQuestion ? this.getPreviousStep(item, QuestionStep.StepPreparation) : item.questionsPreparation;
            default:
                return undefined;
        }
    };

    public static getNextStep = (item: IMapItem, step: QuestionStep): ContextQuestionList | undefined => {
        switch (step) {
            case QuestionStep.Infos:
                return item.questionsProduit?.endQuestion ? this.getNextStep(item, QuestionStep.StepProduit) : item.questionsProduit;
            case QuestionStep.StepTva:
                return item.questionsProduit?.endQuestion ? this.getNextStep(item, QuestionStep.StepProduit) : item.questionsProduit;
            case QuestionStep.StepProduit:
                return item.questionsPose?.endQuestion ? this.getNextStep(item, QuestionStep.StepPose) : item.questionsPose;
            case QuestionStep.StepPose:
                return item.questionsSupport?.endQuestion ? this.getNextStep(item, QuestionStep.StepSupport) : item.questionsSupport;
            case QuestionStep.StepSupport:
                return item.questionsPreparation?.endQuestion ? this.getNextStep(item, QuestionStep.StepPreparation) : item.questionsPreparation;
            case QuestionStep.StepPreparation:
                return item.questionsFinition?.endQuestion ? this.getNextStep(item, QuestionStep.StepFinitions) : item.questionsFinition;
            default:
                return undefined;
        }
    };

    //#endregion
}


const toQuestionContextType = (type: RoomType) => {
    switch (type) {
        case RoomType.Wall:
            return QuestionContextType.Walls;
        case RoomType.Stairs:
            return QuestionContextType.Stairs;
        case RoomType.Sol:
        default:
            return QuestionContextType.Rooms;
    }
}